/**
 * Class Resource Display Config — combo points (assassin stars).
 *
 * Drives the ComboPointSystem (build/spend/reset rules) and the overhead
 * star pips PlayerRenderer floats above the LOCAL player. Same
 * "defaults + dev override → merged" pattern as nameplate.config.ts:
 * tune CLASS_RESOURCE_DEV_OVERRIDE while iterating, promote to defaults
 * once happy. Consumers import CLASS_RESOURCE only.
 *
 * Colors are numeric hex (0xRRGGBB) — the overhead pips render through
 * the canvas NameTag substrate, not CSS. (The unit-frame counter in
 * PlayerInfoPanel uses theme tokens instead; this config is canvas-side.)
 */

export interface ClassResourceOverheadStyle {
  // NOTE: the pip row's vertical POSITION is not configured here — it comes
  // from the overhead stack ('classResource' row) in nameplate.config.ts
  // (NAMEPLATE_STACK_OFFSETS). This config owns styling only.
  /** Star glyph font size in px. */
  fontSize: number;
  /** Star color (hex). Matches the gold accent family (castBar fill / theme gold). */
  color: number;
  /** Black outline width in px so stars pop against bright terrain. */
  strokeWidth: number;
  /** The pip glyph, repeated once per point. */
  starChar: string;
}

export interface ClassResourceConfig {
  /** Combo point cap. */
  maxPoints: number;
  /** Reset to 0 after this long with no combo-relevant cast (out-of-combat decay). */
  outOfCombatResetMs: number;
  /** TOML ability tags that BUILD one point on a successful local cast. */
  builderTags: string[];
  /** TOML ability tags that SPEND (zero) the pool on cast. */
  spenderTags: string[];
  /** Tags that exempt an ability from combo logic entirely (passives). */
  excludeTags: string[];
  /** Classes whose casts participate in combo building (currently rogue only). */
  classes: string[];
  overhead: ClassResourceOverheadStyle;
}

export const CLASS_RESOURCE_DEFAULTS: ClassResourceConfig = {
  maxPoints: 5,
  outOfCombatResetMs: 15000,
  // From content/abilities/rogue.toml: sinister_strike/mutilate
  // carry 'combo_point'; marked_for_death/premeditation carry 'combo_gen';
  // perforate carries 'combo_builder'. ('extra_combo_point' is a passive —
  // excluded via the passive tag, and not a builder tag anyway.)
  builderTags: ['combo_point', 'combo_gen', 'combo_builder'],
  // All rogue finishers (eviscerate, dispatch, envenom, rupture, ...).
  spenderTags: ['finisher'],
  excludeTags: ['passive'],
  classes: ['rogue'],
  overhead: {
    fontSize: 14,
    color: 0xffd700,
    strokeWidth: 3,
    starChar: '◆',
  },
};

/** Partial override layered on top of the defaults — leave `{}` to ship
 *  production defaults. Example:
 *    overhead: { ...CLASS_RESOURCE_DEFAULTS.overhead, fontSize: 18 } */
export const CLASS_RESOURCE_DEV_OVERRIDE: Partial<ClassResourceConfig> = {};

export const CLASS_RESOURCE: ClassResourceConfig = {
  ...CLASS_RESOURCE_DEFAULTS,
  ...CLASS_RESOURCE_DEV_OVERRIDE,
};
